import { eventChannel } from "redux-saga";
import { call, put, take, fork, race, delay } from "redux-saga/effects";

// Coordination states
const COORDINATION_STATES = {
  IDLE: "IDLE",
  TOKEN_REFRESHING: "TOKEN_REFRESHING",
};

// Create a coordination channel for inter-saga communication
let coordinationChannel = null;
let currentState = COORDINATION_STATES.IDLE;
let pendingActions = [];

// Initialize the coordination channel
function createCoordinationChannel() {
  return eventChannel((emitter) => {
    // Store the emitter for later use
    coordinationChannel = {
      emit: emitter,
      close: () => emitter("CLOSE"),
    };

    return () => {
      coordinationChannel = null;
    };
  });
}

// Coordinator saga that manages the coordination state
function* coordinatorSaga() {
  const channel = yield call(createCoordinationChannel);

  try {
    while (true) {
      const event = yield take(channel);

      if (event === "CLOSE") {
        break;
      }

      switch (event.type) {
        case "TOKEN_REFRESH_START":
          currentState = COORDINATION_STATES.TOKEN_REFRESHING;
          console.log(
            "SagaCoordinator: Token refresh started - blocking other sagas"
          );
          break;

        case "TOKEN_REFRESH_COMPLETE":
          currentState = COORDINATION_STATES.IDLE;
          console.log(
            "SagaCoordinator: Token refresh completed - releasing blocked sagas"
          );
          // Process any pending actions
          yield call(processPendingActions);
          break;

        case "TOKEN_REFRESH_ERROR":
          currentState = COORDINATION_STATES.IDLE;
          console.log(
            "SagaCoordinator: Token refresh failed - releasing blocked sagas"
          );
          // Process any pending actions even on error
          yield call(processPendingActions);
          break;
      }
    }
  } finally {
    coordinationChannel = null;
  }
}

// Process any actions that were queued during token refresh
function* processPendingActions() {
  while (pendingActions.length > 0) {
    const action = pendingActions.shift();
    // Re-dispatch the action
    yield put(action);
    // Small delay to prevent overwhelming the system
    yield delay(10);
  }
}

// Signal that token refresh has started
export function signalTokenRefreshStart() {
  if (coordinationChannel) {
    coordinationChannel.emit({ type: "TOKEN_REFRESH_START" });
  }
}

// Signal that token refresh has completed successfully
export function signalTokenRefreshComplete() {
  if (coordinationChannel) {
    coordinationChannel.emit({ type: "TOKEN_REFRESH_COMPLETE" });
  }
}

// Signal that token refresh has failed
export function signalTokenRefreshError() {
  if (coordinationChannel) {
    coordinationChannel.emit({ type: "TOKEN_REFRESH_ERROR" });
  }
}

// Check if sagas should wait (token refresh in progress)
export function shouldWaitForTokenRefresh() {
  return currentState === COORDINATION_STATES.TOKEN_REFRESHING;
}

// Wait for token refresh to complete if it's in progress
export function* waitForTokenRefreshIfNeeded() {
  if (currentState === COORDINATION_STATES.TOKEN_REFRESHING) {
    console.log("SagaCoordinator: Waiting for token refresh to complete...");

    // Wait for the state to change back to IDLE
    while (currentState === COORDINATION_STATES.TOKEN_REFRESHING) {
      yield delay(100); // Check every 100ms
    }

    console.log(
      "SagaCoordinator: Token refresh completed, proceeding with saga"
    );
  }
}

// Queue an action to be processed after token refresh completes
export function queueActionForLater(action) {
  if (shouldWaitForTokenRefresh()) {
    pendingActions.push(action);
    return true; // Action was queued
  }
  return false; // Action should be processed immediately
}

// Wrapper function to coordinate saga execution
export function* coordinatedSagaWrapper(sagaFunction, ...args) {
  // Wait for token refresh to complete if it's in progress
  yield call(waitForTokenRefreshIfNeeded);

  // Now execute the original saga
  yield call(sagaFunction, ...args);
}

// Main coordinator saga to be included in root saga
export function* sagaCoordinator() {
  yield fork(coordinatorSaga);
}

// Get current coordination state (for debugging)
export function getCurrentCoordinationState() {
  return currentState;
}

// Get pending actions count (for debugging)
export function getPendingActionsCount() {
  return pendingActions.length;
}
